import API from '@/services';

import { trackStartLoadHistory, trackFinishLoadHistory } from '@/utils/historyHandler';
import { action, observable } from 'mobx';
import { HISTORY_STATUS, NO_MORE_REASON } from '@/types/history';
import { GET_HISTORY_MESSAGE_COUNT, GetMsgResCode } from './../utils/constants';
import { UserEvents, UserEventStatus } from './user/constants';
import { HistoryServiceRequest } from '@shopee/shopee-chatbot-agent-chat/dist/types';
import { getChatHistory } from '@shopee/shopee-chatbot-agent-chat/dist/history';
import { handleTerminalConfict } from '@/services/handleTerminalConfict';

import formatHistoryMessages from '@/utils/formatHistoryMessages';
import userStore from '@/stores/user';
import firstInitStore from '@/stores/firstInit';
import reporter from '@/utils/report';
import { getChatbotHost, getConfig, isBank, isDriver, isSupportChatbot, UseLiveChatSDKApp } from '@/config/core';
import { getChatbotApiHeaders } from '@/services/request';

export class HistoryStore {
  @observable historyStatus: HISTORY_STATUS = HISTORY_STATUS.HAS_MORE_HISTORY; // 是否还有历史消息

  @observable initMessageId = ''; // 第一条messageId

  @observable beforeMessageId = ''; // 最新一条历史消息的id

  @observable historyList: Array<any> = []; // 历史消息列表

  @observable lastMessageTime: number = new Date().getTime(); // 上一条的时间

  @observable lastShowTime: number = new Date().getTime(); // 上一个显示的时间

  @observable isRecoveryHistory = false; // 是否恢复历史

  @observable hasShownLiveChatMsgIds: { [key: string]: number; } = {}; // 已经显示了的客服消息id列表

  @observable hasShownLiveChatMsgSeqIds: { [key: string]: number; } = {};

  @action.bound
  async fetchChatHistory(params: HistoryServiceRequest) {
    const { historyParams } = getConfig();

    // 历史消息加载
    reporter.apiBatchReport({ type: 'count', value: { label: 'func_history_msg_load' }});
    trackStartLoadHistory();

    const useChatbotApi = (isBank() || isDriver()) && !!isSupportChatbot() && UseLiveChatSDKApp.isHitChatbotGray;
    const data = await getChatHistory(
      useChatbotApi ? 0 : historyParams.app, // 传0表示使用chatbot历史接口
      {
        headers: historyParams?.axiosConfig?.getHeaders?.(),
        baseURL: useChatbotApi ? '' : historyParams?.axiosConfig?.baseURL,
      },
      params,
      (fetch) => {
        fetch.interceptors.request.use((config) => {
          if (config.url?.startsWith('/api')) {
            config.baseURL = getChatbotHost();

            const headers = getChatbotApiHeaders();
            Object.keys(headers).forEach((v) => {
              config.headers.common[v] = (headers as any)[v];
            });
          }

          return config;
        });
      },
    );
    const isSuccess = !(data instanceof Error);

    if (isSuccess) {
      trackFinishLoadHistory();
    } else {
      // @ts-ignore
      const code = data?.response?.data?.code;

      if (code === GetMsgResCode.MULTI_TERMINAL_CONFLICT) {
        handleTerminalConfict();
      }
    }

    return data;
  }

  @action.bound
  async getChatHistory() {
    await firstInitStore.initPromise;
    const { historyParams } = getConfig();
    // 只有支持chatbot的情况下没有beforeMessageId的时候不支持获取chat history，防止接口报错,不然会导致无法获取历史消息
    if (this.beforeMessageId.length === 0 && isSupportChatbot() && UseLiveChatSDKApp.isHitChatbotGray) {
      return { has_more: true };
    }

    const params = {
      message_id: this.beforeMessageId,
      limit: GET_HISTORY_MESSAGE_COUNT,
      ...historyParams?.extraParams,
    };
    const data = await this.fetchChatHistory(params);

    if (data instanceof Error) {
      return { has_more: true };
    }

    // 上报显示历史消息提示语并获取历史消息成功
    if (userStore.userEvents[UserEvents.GetHistoryPrompt] === UserEventStatus.NonExist) {
      // 发给后端
      (API as { reportUserEvents: Function }).reportUserEvents({ event_id: UserEvents.GetHistoryPrompt });
      // 更新store
      userStore.UPDATE_USER_EVENTS({ [UserEvents.GetHistoryPrompt]: UserEventStatus.Exist });
    }

    // 历史消息加载成功
    reporter.apiBatchReport({ type: 'count', value: { label: 'func_history_msg_load_ok' }});

    // @ts-ignore
    const hasMore = data.has_more;
    // @ts-ignore
    const noMoreReason = data.no_more_reason;
    const noMoreReasonMapping = {
      [NO_MORE_REASON.NO_MORE_HISTORY]: HISTORY_STATUS.NO_MORE_HISTORY,
      [NO_MORE_REASON.NO_MORE_HISTORY_IN_180_DAYS]: HISTORY_STATUS.NO_MORE_HISTORY_IN_180_DAYS,
    };
    // @ts-ignore;
    const { messages: formatDataList, beforeMessageId } = formatHistoryMessages(data.messages || []);
    this.setBeforeMessageId(beforeMessageId);

    const newHistoryList = [...formatDataList, ...this.historyList];
    // @ts-ignore
    const status: number = hasMore ? HISTORY_STATUS.HAS_MORE_HISTORY : noMoreReasonMapping[noMoreReason];

    this.setHistoryStatus(status);
    this.setHistoryList(newHistoryList);

    return {
      has_more: hasMore,
      messagesList: formatDataList,
    };
  }

  @action
  setLastMessageTime(lastMessageTime: number) {
    this.lastMessageTime = lastMessageTime;
  }

  @action
  setLastShowTime(lastShowTime: number) {
    this.lastShowTime = lastShowTime;
  }

  @action
  setIsRecoveryHistory(isRecoveryHistory: boolean) {
    this.isRecoveryHistory = isRecoveryHistory;
  }

  @action
  setBeforeMessageId(beforeMessageId: string) {
    this.beforeMessageId = beforeMessageId;
  }

  @action
  setInitMessageId(initMessageId: string) {
    this.initMessageId = initMessageId;
  }

  @action
  setHistoryStatus(status: HISTORY_STATUS) {
    this.historyStatus = status;
  }

  @action
  setHistoryList(historyList: Array<any>) {
    this.historyList = historyList;
  }

  @action
  setHasShownLiveChatMsgIds(hasShownLiveChatMsgIds: {
    [key: string]: number;
  }) {
    this.hasShownLiveChatMsgIds = hasShownLiveChatMsgIds;
  }

  @action
  setHasShownLiveChatMsgSeqIds(hasShownLiveChatMsgSeqIds: {
    [key: string]: number;
  }) {
    this.hasShownLiveChatMsgSeqIds = hasShownLiveChatMsgSeqIds;
  }

  @action
  updateHasShownLiveChatMsgIds(chatbotMessageId: string) {
    // 1就是占位的没啥用
    this.hasShownLiveChatMsgIds = {
      ...this.hasShownLiveChatMsgIds,
      [chatbotMessageId]: 1,
    };
  }

  @action
  updateHasShownLiveChatMsgSeqIds(seqId: string) {
    this.hasShownLiveChatMsgSeqIds = {
      ...this.hasShownLiveChatMsgSeqIds,
      [seqId]: 1,
    };
  }

  @action
  UPDATE_DIALOGUE({ obj = {}, messageId }: { obj: { [key: string]: unknown }, messageId: string }) {
    const dialogue = this.historyList.find(i => i.message_id === messageId);
    if (!dialogue) {
      return;
    }

    const propertyList = [
      'summary',
      'supportingMessage',
    ];
    // 此处可按需加更新的数据

    propertyList.forEach((property) => {
      if (obj.hasOwnProperty(property)) {
        dialogue.data = {
          ...dialogue.data,
          [property]: obj[property],
        };
      }
    });

    this.setHistoryList([...this.historyList]);
  }

  @action
  UPDATE_DIALOGUE_OUTER_DATA({ obj = {}, messageId }: { obj: { [key: string]: unknown }, messageId: string }) {
    // 修改dialogue的外层数据，非data中的数据
    const dialogue = this.historyList.find(i => i.message_id === messageId);

    if (!dialogue) {
      return;
    }

    const propertyList = [
      'showMaxWidth', // 展示最大宽度
      'showReloadIcon', // 展示刷新按钮
      'onReloadClick', // 点击刷新按钮
    ];

    propertyList.forEach((property) => {
      if (obj.hasOwnProperty(property)) {
        dialogue[property] = obj[property as keyof typeof obj];
      }
    });

    this.setHistoryList([...this.historyList]);
  }
}

const historyStore = new HistoryStore();

export default historyStore;
